import { Chip } from '@mui/material'
import { DataGrid, GridColDef, GridPaginationMeta, GridPaginationModel, GridRowParams, GridToolbar } from '@mui/x-data-grid'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useDispatch } from 'react-redux'
import FlowApiClient from '../../backend/sub_api_clients/FlowApiClient'
import Formatter from '../../helpers/Formatter'
import { changeRoute } from '../../store/slices/SessionSlice'
import useQuery, { buildQueryKey } from '../hooks/useQuery'

const columns: GridColDef<any>[] = [
    { field: 'name', headerName: 'Name', width: 400, renderCell: params => params.row.name?.trim() },
    { field: 'runType', headerName: 'Run type', renderCell: params => <Chip size='small' color={params.row?.config?.samplePercentage ? 'dryRun' : 'primary'} label={params.row?.config?.samplePercentage ? 'dry-run' : 'full'} /> },
    { field: 'startedAt', headerName: 'Started at', valueGetter: (_, row) => new Date(row.startedAt), width: 300, valueFormatter: value => Formatter.date(new Date(value), 'mm/dd/yyyy hh:mm:ss') },
    { field: 'config.settlementInstance', headerName: 'Settlement Instance', renderCell: params => params.row?.config?.settlementInstance?.trim(), width: 120 },
    { field: 'errors', headerName: 'Errors', renderCell: params => params.row?.errors?.length ?? 0 }
]

const slots: any = { toolbar: GridToolbar }
const slotProps: any = {
    toolbar: {
        showQuickFilter: true,
        quickFilterProps: {
            variant: 'outlined',
            size: 'small'
        }
    },
    loadingOverlay: {
        variant: 'skeleton',
        noRowsVariant: 'skeleton'
    }
}
const sx = {
    '&.MuiDataGrid-root': {
        padding: 3,
        borderRadius: '12px'
    }, '& .MuiDataGrid-cell:focus': {
        outline: 'none'
    },
    '& .MuiDataGrid-cell:focus-within': {
        outline: 'none'
    }
}

const PAGE_SIZE = 50

const FlowsPrevRuns = () => {
    const dispatch = useDispatch()
    const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: PAGE_SIZE })
    const [rowCountState, setRowCountState] = useState(0)
    const [searchParam, setSearchParam] = useState(undefined)
    const paginationMetaRef = useRef<GridPaginationMeta>(undefined)
    const mapPageToNextCursor = useRef<{ [page: number]: string }>({})

    const { status, response } = useQuery({
        queryKey: buildQueryKey(`${searchParam ? `getSettlementResults-${searchParam}` : 'getSettlementResults'}`, paginationModel.page, mapPageToNextCursor.current[paginationModel.page - 1] ?? 'first'),
        queryFn: () => FlowApiClient.getSettlementResults({ batchSize: PAGE_SIZE, nextToken: mapPageToNextCursor.current[paginationModel.page - 1], searchParam: searchParam }),
        cache: true
    })

    const rows = useMemo(() => response?.items ?? [], [response?.items])

    const paginationMeta = useMemo(() => {
        if (response?.hasNextPage !== undefined && paginationMetaRef.current?.hasNextPage !== response?.hasNextPage) {
            paginationMetaRef.current = { hasNextPage: response?.hasNextPage }
        }
        return paginationMetaRef.current
    }, [response?.hasNextPage])

    useEffect(() => {
        if (!(status === 'loading') && response?.nextToken) {
            mapPageToNextCursor.current[paginationModel.page] = response?.nextToken
        }
    }, [paginationModel.page, status === 'loading', response?.nextToken])

    useEffect(() => {
        if (!paginationMeta?.hasNextPage) {
            setRowCountState(paginationModel.page * PAGE_SIZE + (response?.items?.length ?? 0))
        } else {
            setRowCountState(-1)
        }
    }, [paginationMeta?.hasNextPage, paginationModel.page])

    const handlePaginationModelChange = useCallback((newPaginationModel: GridPaginationModel) => {
        if (newPaginationModel.page === 0 || mapPageToNextCursor.current[newPaginationModel.page - 1]) {
            setPaginationModel(newPaginationModel)
        }
    }, [])

    const handleRowClick = useCallback((row: GridRowParams<any>) => {
        const rows = response?.items?.find(f => f.uuid === row.id)
        if (rows) {
            dispatch(changeRoute({
                route: 'multirun-result',
                params: { contractUUIDs: rows.contractUUIDs, eeUUIDs: rows.executionEnvironmentUUIDs, config: rows.config }
            }))
        }
    }, [rows])

    return (
        <DataGrid
            sx={sx}
            loading={status === 'loading' || status === 'stale'}
            getRowId={row => row.uuid}
            columns={columns}
            rows={rows}
            paginationMode='server'
            paginationMeta={paginationMeta}
            paginationModel={paginationModel}
            onPaginationModelChange={handlePaginationModelChange}
            filterMode='server'
            onFilterModelChange={m => setSearchParam(m.quickFilterValues[0])}
            filterDebounceMs={500}
            rowCount={rowCountState}
            pageSizeOptions={[PAGE_SIZE]}
            onRowClick={handleRowClick}
            slots={slots}
            slotProps={slotProps}
        />
    )
}

export default FlowsPrevRuns
